/**
 * Particle System for Starry Background
 */

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  twinkleSpeed: number;
  twinklePhase: number;
}

interface ShootingStar {
  x: number;
  y: number;
  vx: number;
  vy: number;
  length: number;
  opacity: number;
  active: boolean;
}

export class ParticleBackground {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private shootingStars: ShootingStar[] = [];
  private animationId: number | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.resize();
    this.initParticles();
    window.addEventListener('resize', () => this.resize());
  }

  private resize() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
  }

  private initParticles() {
    const particleCount = Math.floor((this.canvas.width * this.canvas.height) / 8000);

    for (let i = 0; i < particleCount; i++) {
      this.particles.push({
        x: Math.random() * this.canvas.width,
        y: Math.random() * this.canvas.height,
        vx: (Math.random() - 0.5) * 0.1,
        vy: (Math.random() - 0.5) * 0.1,
        radius: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.8 + 0.2,
        twinkleSpeed: Math.random() * 0.02 + 0.01,
        twinklePhase: Math.random() * Math.PI * 2
      });
    }

    // Initialize shooting stars
    for (let i = 0; i < 3; i++) {
      this.shootingStars.push({
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        length: 0,
        opacity: 0,
        active: false
      });
    }
  }

  private createShootingStar() {
    const star = this.shootingStars.find(s => !s.active);
    if (!star) return;

    star.x = Math.random() * this.canvas.width;
    star.y = Math.random() * this.canvas.height * 0.3;
    star.vx = Math.random() * 3 + 2;
    star.vy = Math.random() * 2 + 1;
    star.length = Math.random() * 80 + 40;
    star.opacity = 1;
    star.active = true;
  }

  private updateParticles() {
    this.particles.forEach(p => {
      p.x += p.vx;
      p.y += p.vy;

      // Wrap around edges
      if (p.x < 0) p.x = this.canvas.width;
      if (p.x > this.canvas.width) p.x = 0;
      if (p.y < 0) p.y = this.canvas.height;
      if (p.y > this.canvas.height) p.y = 0;

      // Twinkle effect
      p.twinklePhase += p.twinkleSpeed;
    });

    // Update shooting stars
    this.shootingStars.forEach(star => {
      if (!star.active) return;

      star.x += star.vx;
      star.y += star.vy;
      star.opacity -= 0.01;

      if (star.opacity <= 0 || star.x > this.canvas.width || star.y > this.canvas.height) {
        star.active = false;
      }
    });

    // Randomly create shooting stars
    if (Math.random() < 0.002) {
      this.createShootingStar();
    }
  }

  private drawParticles() {
    this.particles.forEach(p => {
      const twinkle = Math.sin(p.twinklePhase) * 0.3 + 0.7;
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      this.ctx.fillStyle = `rgba(255, 255, 255, ${p.opacity * twinkle})`;
      this.ctx.fill();

      // Add glow for larger particles
      if (p.radius > 1) {
        const gradient = this.ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius * 3);
        gradient.addColorStop(0, `rgba(200, 180, 255, ${p.opacity * twinkle * 0.3})`);
        gradient.addColorStop(1, 'rgba(200, 180, 255, 0)');
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(p.x - p.radius * 3, p.y - p.radius * 3, p.radius * 6, p.radius * 6);
      }
    });
  }

  private drawShootingStars() {
    this.shootingStars.forEach(star => {
      if (!star.active) return;

      const gradient = this.ctx.createLinearGradient(
        star.x, star.y,
        star.x - star.vx * star.length / 5, star.y - star.vy * star.length / 5
      );
      gradient.addColorStop(0, `rgba(255, 255, 255, ${star.opacity})`);
      gradient.addColorStop(0.5, `rgba(180, 200, 255, ${star.opacity * 0.6})`);
      gradient.addColorStop(1, 'rgba(180, 200, 255, 0)');

      this.ctx.strokeStyle = gradient;
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.moveTo(star.x, star.y);
      this.ctx.lineTo(star.x - star.vx * star.length / 5, star.y - star.vy * star.length / 5);
      this.ctx.stroke();
    });
  }

  private animate = () => {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.updateParticles();
    this.drawParticles();
    this.drawShootingStars();
    this.animationId = requestAnimationFrame(this.animate);
  }

  start() {
    this.animate();
  }

  stop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
}
